import {useCallback, useState} from "react";
import {call, takeLatest} from "redux-saga/effects";

import {channel} from "redux-saga";
import {actions, useSagaCallback, useSagaEffect} from "../store/sagas";
import {useRecaptcha} from "../utils/recaptcha";
import {apiGet, apiPost, apiPostWithCaptcha} from "./client";
import {loadUserProfile} from "./user";

function* loadMessages(args) {
    const params = new URLSearchParams(args);
    const url = `/api/v2/message/messages.json?${params}`;
    const response = yield call(apiGet, url);
    const comments = [];
    for (const message of response.messages) {
        const { comment } = message;
        if (comment) {
            delete message.comment;
            message.comment_id = comment.id;
            comments.push(comment);
        }
    }
    if (comments.length) {
        yield actions.commentsLoaded(comments);
    }
    return response;
}

/**
 * Fetch inbox.
 * @param feedbackCh
 * @param setMessages:{function (messages:[{is_community_mail: boolean, author: string, subject: string, created: number, type: number, community: string, is_muted: boolean, content: string, is_deleted: boolean, post_id: number, is_archived: boolean, conversation_id: number, is_unread: boolean, recipient: string, outbound_link: string, id: number}]): void}
 * @param type:{'all'|'sent'|'messages'|'comments'|'unread'}
 * @param page:{number}
 * @returns {{loadNextPage:(function(): void)}}
 */
export const useMessages = (feedbackCh, setMessages, type = 'all', page = 1) => {
    const [atEnd, setAtEnd] = useState(false);
    const [actionCh] = useState(channel);
    useSagaEffect(feedbackCh, useCallback(function*() {
        function* afterLoading() {
            if (type === 'unread') {
                // Update unread count.
                yield call(loadUserProfile);
            }
        }
        setMessages(null);
        setAtEnd(false);
        let currentPage = page;
        const pageIncrement = type !== 'unread' ? 1 : 0;
        yield takeLatest(actionCh, function* ({ loadType }) {
            try {
                switch (loadType) {
                    case 'loadNextPage':
                        const response = yield call(loadMessages, { type, page: currentPage + pageIncrement });
                        if (!response.has_more_entries) {
                            setAtEnd(true);
                        }
                        setMessages((oldMessages) => [
                            ...oldMessages,
                            ...response.messages,
                        ]);
                        currentPage += pageIncrement;
                        yield call(afterLoading);
                        break;
                    default:
                        return;
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        });
        const response = yield call(loadMessages, { type, page });
        if (!response.has_more_entries) {
            setAtEnd(true);
        }
        setMessages(response.messages);
        yield call(afterLoading);
    }, [type, page, setAtEnd]));
    const loadNextPage = useCallback(() => {
        if (!atEnd) {
            actionCh.put({ loadType: 'loadNextPage' })
        }
    }, [actionCh, atEnd]);
    return { loadNextPage };
}

/**
 * Fetch all messages with the given conversation_id.
 * @param id:{number}
 * @returns {[{is_community_mail: boolean, author: string, subject: string, created: number, type: number, community: string, is_muted: boolean, content: string, is_deleted: boolean, post_id: number, is_archived: boolean, conversation_id: number, is_unread: boolean, recipient: string, outbound_link: string, id: number}]}
 */
export function* getMessage(id) {
    const response = yield call(apiGet, `/api/v2/message/message.json?id=${id}`);
    return response.messages;
}

/**
 *
 * @param feedbackCh
 * @returns {function(recipient:string, subject:string, content:string): Promise<unknown>}
 */
export const useCreateMessage = (feedbackCh) => {
    const recaptcha = useRecaptcha();
    return useSagaCallback(feedbackCh, useCallback(function* ({ recipient, subject, content }) {
        const url = `/api/v2/action/create_message`;
        const data = { recipient, subject, content, liability: true };
        return yield call(apiPostWithCaptcha, url, data, recaptcha);
    }, [recaptcha]));
};

export const useCreateReply = (feedbackCh) => {
    const recaptcha = useRecaptcha();
    return useSagaCallback(feedbackCh, useCallback(function* (parentId, content) {
        const url = `/api/v2/action/create_message`;
        const data = { parentId, content, liability: true };
        let response;
        try {
            response = yield call(apiPost, url, data);
        } catch (error) {
            if (error.message === 'captcha required') {
                const token = yield call(recaptcha);
                response = yield call(apiPost, url, { ...data, "captcha-response": token });
            } else {
                throw error;
            }
        }
        return response;
    }, [recaptcha]));
};

export const useMarkMessageUnread = (feedbackCh, setMessages) => {
    return useSagaCallback(feedbackCh, useCallback(function* (id, unread) {
        setMessages((messages) => messages.map((message) => message.id === id ? { ...message, is_unread: unread} : message));
        try {
            yield call(apiPost, `/api/v2/action/set_${unread ? 'un' : ''}read`, { id });
        } catch (error) {
            setMessages((messages) => messages.map((message) => message.id === id ? { ...message, is_unread: !unread} : message));
        }
    }, [feedbackCh, setMessages]));
};
